import { Component, OnInit } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { SharedModule } from '../../shared/shared.module';
import { ErrorPanelService } from '../../services/error-panel.service';
import { TokenService } from '../../services/token.service';
import { environment } from '../../../environments/environment';
import { PaymentsService } from '../../services/payments.service';
import { firstValueFrom } from 'rxjs';
import { SignInOutputData } from '../../models/doctor-model';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { TermsDialogComponent } from './components/terms-dialog/terms-dialog.component';
import { Inject, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FiscalCodePanelComponent } from '../../shared/fiscal-code-panel/fiscal-code-panel.component';
import { DoctorService } from '../../services/doctor.service';

@Component({
  selector: 'app-sing-in-page',
  standalone: true,
  imports: [
    SharedModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    FormsModule,
  ],
  templateUrl: './sign-in-page.component.html',
  styleUrl: './sign-in-page.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  animations: [
    trigger('fadeInLeft', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-20px)' }),
        animate(
          '0.6s ease-out',
          style({ opacity: 1, transform: 'translateX(0)' })
        ),
      ]),
    ]),
    trigger('fadeInRight', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate(
          '0.6s ease-out',
          style({ opacity: 1, transform: 'translateX(0)' })
        ),
      ]),
    ]),
    trigger('fadeInUp', [
      transition(
        ':enter',
        [
          style({ opacity: 0, transform: 'translateY(20px)' }),
          animate(
            '0.6s {{delay}} ease-out',
            style({ opacity: 1, transform: 'translateY(0)' })
          ),
        ],
        { params: { delay: '0ms' } }
      ),
    ]),
  ],
})
export class SignInPageComponent implements OnInit {
  showLoader: boolean = false;
  isLoading: boolean = false;
  private readonly isBrowser: boolean;

  welcomeText: string =
    'Il primo <strong>software AI</strong> per <strong>psicologi</strong> e <strong>psicoterapeuti</strong> che ottimizza e rende più <strong>efficace</strong> il tuo <strong>lavoro</strong> con i pazienti.';

  constructor(
    private router: Router,
    private authService: AuthService,
    private errorPanelService: ErrorPanelService,
    private paymentsService: PaymentsService,
    private route: ActivatedRoute,
    private tokenService: TokenService,
    private dialog: MatDialog,
    private doctorService: DoctorService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    this.route.queryParams.subscribe((params) => {
      const code = params['code'];
      const returnedState = params['state'];
      const storedState = sessionStorage.getItem('oauthState');
      const storedTimestamp = sessionStorage.getItem('oauthStateTimestamp');

      // Clear stored OAuth state data
      const clearOAuthData = () => {
        sessionStorage.clear();
      };

      if (!code || !returnedState) {
        clearOAuthData();
        return;
      }

      // Validate state and timestamp
      if (!storedState || !storedTimestamp) {
        clearOAuthData();
        return;
      }

      // Check if the state is not expired (15 minutes max)
      const stateAge = Date.now() - parseInt(storedTimestamp);
      if (stateAge > 15 * 60 * 1000) {
        // 15 minutes in milliseconds
        clearOAuthData();
        this.errorPanelService.showErrorSnackBar(
          'La sessione di autenticazione è scaduta. Per favore riprova.'
        );
        return;
      }

      if (
        params['scope'] &&
        !params['scope'].includes('https://www.googleapis.com/auth/calendar')
      ) {
        this.errorPanelService.showErrorSnackBar(
          "Abilitare l'accesso al calendario Google per poter utilizzare PsicoGenius."
        );
        this.router.navigate(['/']);
        return;
      }
      if (
        params['scope'] &&
        !params['scope'].includes('https://www.googleapis.com/auth/gmail.send')
      ) {
        this.errorPanelService.showErrorSnackBar(
          "Abilitare l'accesso alla mail Google per poter utilizzare PsicoGenius."
        );
        this.router.navigate(['/']);
        return;
      }

      if (code && returnedState === storedState) {
        this.isLoading = true;
        this.exchangeGoogleCodeForTokens(returnedState, code);
      } else if (returnedState !== storedState) {
        clearOAuthData();
        this.errorPanelService.showErrorSnackBar(
          'Errore di autenticazione. Per favore riprova.'
        );
      }
    });
  }
  navigateToPrivacyPage() {
    this.router.navigate(['/informativa-privacy']);
  }

  navigateToTermsOfServicePage() {
    this.router.navigate(['/termini-di-servizio']);
  }

  private async exchangeGoogleCodeForTokens(state: string, code: string) {
    if (!this.isBrowser) {
      return;
    }

    try {
      const response: SignInOutputData = await firstValueFrom(
        this.authService.getSignInOutputData(state, code)
      );

      this.storeTokens(response);
      sessionStorage.setItem('role', response.role);

      if (
        response.is_new_user ||
        response.role === 'existingDoctorWithoutStripeAccount'
      ) {
        // NOTE: the role "existingDoctor..." means the, in the backend, th user has
        // no stripe subscription associated to him. TODO: check if we can remove this.
        console.log('New user: opening Stripe checkout directly');
        this.openFiscalCodePanel();
      } else if (response.role === 'admin' || response.role === 'doctorPayer') {
        this.router.navigate(['/homepage']);
      } else {
        console.log('Existing user: opening homepage');
        this.router.navigate(['/subscription/renew']);
      }
    } catch (error) {
      this.handleAuthError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private storeTokens(response: any) {
    if (!this.isBrowser) {
      return;
    }

    sessionStorage.setItem('profilePicture', response.avatar_blob_name);
    this.tokenService.setGoogleAccessToken(response.access_token_google, 3600);
    this.tokenService.setPsicogeniusAccessToken(
      response.access_token_psicogenius,
      3600
    );
  }

  private handleAuthError(error: any) {
    console.error('Authentication error:', error);
    let errorMessage = 'Autenticazione fallita. Per favore riprova.';

    if (error.error && error.error.detail) {
      errorMessage = error.error.detail;
    }

    this.errorPanelService.showErrorSnackBar(errorMessage);
  }

  public async signUpWithGoogle() {
    const dialogRef = this.dialog.open(TermsDialogComponent, {
      width: '1000px',
      maxWidth: '90vw',
      disableClose: true,
      panelClass: 'terms-dialog-container',
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.oauthSignIn(true);
    }
  }

  public oauthSignIn(isSignup: boolean = false) {
    if (!this.isBrowser) {
      return;
    }

    const oauth2Endpoint = 'https://accounts.google.com/o/oauth2/v2/auth';
    const form = document.createElement('form');
    form.setAttribute('method', 'GET');
    form.setAttribute('action', oauth2Endpoint);

    // Generate a secure random state with signup information
    const randomState = crypto
      .getRandomValues(new Uint8Array(32))
      .reduce((str, byte) => str + byte.toString(16).padStart(2, '0'), '');
    const state = isSignup ? `${randomState}_signup` : randomState;

    // Store both state and intent securely
    sessionStorage.setItem('oauthState', state);
    sessionStorage.setItem('oauthIntent', isSignup ? 'signup' : 'login');
    sessionStorage.setItem('oauthStateTimestamp', Date.now().toString());

    const params: { [key: string]: string } = {
      client_id: environment.clientId,
      redirect_uri: environment.baseUrl,
      response_type: 'code',
      scope:
        'https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid',
      include_granted_scopes: 'true',
      state: state,
      access_type: 'offline',
      // Always request consent for signup, use select_account for login
      prompt: isSignup ? 'consent select_account' : 'select_account',
    };

    for (let p in params) {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', p);
      input.setAttribute('value', params[p]);
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }

  openFiscalCodePanel() {
    if (!this.isBrowser) {
      return;
    }

    const dialogRef = this.dialog.open(FiscalCodePanelComponent);

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        const checkoutResponse: any = await firstValueFrom(
          this.paymentsService.createCheckoutSession()
        );
        window.location.href = checkoutResponse.checkout_session_url;
      }
    });
  }

  getAnimationDelay(element: 'title' | 'subtitle' | 'button' | 'privacy'): {
    delay: number;
  } {
    const delays = {
      title: 200,
      subtitle: 400,
      button: 600,
      privacy: 800,
    } as const;
    return { delay: delays[element] };
  }
}
